import type {
  CreateReminderForEmployeesPayload,
  CreateReminderPayload,
  ReminderDetailDto,
  ReminderListItemDto,
  UpdateReminderForEmployeesPayload,
  UpdateReminderForMePayload,
} from '@/modules/Reminder/types/api/reminder'
import type {
  Reminder,
  ReminderFormValues,
  ReminderRecipient,
} from '@/modules/Reminder/types/model'

function toTimeString(date: Date): string {
  return [date.getHours(), date.getMinutes()]
    .map((part) => String(part).padStart(2, '0'))
    .join(':')
}

function recipientNamesToRecipients(names: string[]): ReminderRecipient[] {
  return names.map((name, index) => ({
    id: -(index + 1),
    name,
  }))
}

export function reminderListItemToReminder(dto: ReminderListItemDto): Reminder {
  return {
    id: dto.id,
    title: dto.title,
    description: dto.description ?? '',
    date: new Date(dto.remindAt).toISOString(),
    time: toTimeString(new Date(dto.remindAt)),
    recipients: recipientNamesToRecipients(dto.recipientNames),
    isCreator: dto.isCreator,
  }
}

export function reminderDetailToFormValues(dto: ReminderDetailDto): {
  values: ReminderFormValues
  recipients: ReminderRecipient[]
} {
  const remindAt = new Date(dto.remindAt)
  const dateOnly = [
    remindAt.getFullYear(),
    String(remindAt.getMonth() + 1).padStart(2, '0'),
    String(remindAt.getDate()).padStart(2, '0'),
  ].join('-')

  const recipients = dto.recipients.map((recipient) => ({
    id: recipient.id,
    name: recipient.name,
  }))

  return {
    values: {
      title: dto.title,
      description: dto.description ?? '',
      assignToOthers: recipients.length > 0,
      recipientIds: recipients.map((recipient) => recipient.id),
      date: dateOnly,
      time: toTimeString(remindAt),
    },
    recipients,
  }
}

export function buildRemindAt(
  dateValue: string,
  timeValue: string | null
): string {
  const [year, month, day] = dateValue.slice(0, 10).split('-').map(Number)
  const [hours, minutes] = timeValue
    ? timeValue.split(':').map(Number)
    : [12, 0]

  return new Date(year, month - 1, day, hours, minutes, 0, 0).toISOString()
}

function formValuesToBasePayload(
  values: ReminderFormValues
): CreateReminderPayload {
  return {
    title: values.title.trim(),
    description: values.description?.trim() || null,
    remindAt: buildRemindAt(values.date as string, values.time),
  }
}

export function formValuesToUpdateForMePayload(
  values: ReminderFormValues
): UpdateReminderForMePayload {
  return formValuesToBasePayload(values)
}

export function formValuesToUpdateForEmployeesPayload(
  values: ReminderFormValues
): UpdateReminderForEmployeesPayload {
  return {
    ...formValuesToBasePayload(values),
    employeeIds: values.assignToOthers ? values.recipientIds : [],
  }
}

export function formValuesToCreateForMePayload(
  values: ReminderFormValues
): CreateReminderPayload {
  return formValuesToBasePayload(values)
}

export function formValuesToCreateForEmployeesPayload(
  values: ReminderFormValues
): CreateReminderForEmployeesPayload {
  return {
    ...formValuesToBasePayload(values),
    employeeIds: values.recipientIds,
  }
}
